import { Component, ElementRef, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { AuthService } from '@app/auth/services/auth.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '@app/reducers';
import * as toast from '@core/store/actions/toast.action';
import { Router } from '@angular/router';
import { HelperService } from '@shared/services/helper.service';
import { TranslationService } from '@app/core/service/translation.service';
import { ApiService } from '@core/api.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PasswordPolicyHelper, PasswordPolicyState } from '@app/shared/validators/password-policy.helper';

@Component({
  selector: 'app-forgot-password-reset-form',
  templateUrl: './forgot-password-reset-form.component.html',
  styleUrls: ['./forgot-password-reset-form.component.scss']
})
export class ForgotPasswordResetFormComponent implements OnInit, OnDestroy {
  passwordResetForm: FormGroup;
  errorMessage = '';
  passwordPolicy = new PasswordPolicyState();
  @Input() user: string;
  @Input() token: string;

  private passwordValueChangeSub: Subscription;
  private passwordCheckSub: Subscription;

  get passCheckLoading(): boolean {
    return this.passwordPolicy.loading;
  }

  get hasPasswordDetailError(): boolean {
    return PasswordPolicyHelper.controlHasPolicyError(
      this.passwordResetForm && this.passwordResetForm.get('new_password1')
    );
  }

  constructor(
    private translationService: TranslationService,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private store: Store<fromRoot.AppState>,
    private element: ElementRef,
    private helperService: HelperService,
    private apiService: ApiService,
  ) {}

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy(): void {
    if (this.passwordValueChangeSub) {
      this.passwordValueChangeSub.unsubscribe();
    }
    if (this.passwordCheckSub) {
      this.passwordCheckSub.unsubscribe();
    }
  }

  initForm(): void {
    this.passwordResetForm = this.fb.group(
      {
        user: [this.user, Validators.required],
        token: [this.token, Validators.required],
        new_password1: [null, [
          Validators.required,
          PasswordPolicyHelper.shortPassword,
          PasswordPolicyHelper.commonPasswordValidator(this.passwordPolicy),
        ]],
        new_password2: [null, Validators.required]
      },
      {
        validator: PasswordPolicyHelper.matchPasswords('new_password1', 'new_password2')
      }
    );

    this.passwordValueChangeSub = this.passwordResetForm.controls['new_password1'].valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe((password: string) => {
      this.checkPassword(password);
    });
  }

  private checkPassword(password: string): void {
    this.errorMessage = '';
    this.passwordCheckSub = PasswordPolicyHelper.checkPassword(
      this.apiService,
      password,
      [],
      this.passwordPolicy,
      this.passwordResetForm.controls['new_password1'],
      () => this.cdRef.detectChanges(),
      this.passwordCheckSub,
    );
  }

  submit(formGroup: FormGroup) {
    this.helperService.validationScroll(this.element);
    this.errorMessage = '';

    if (formGroup.valid) {
      this.authService
        .forgotPasswordReset({
          new_password1: formGroup.value.new_password1,
          new_password2: formGroup.value.new_password2,
          user: formGroup.value.user,
          token: formGroup.value.token,
          _ems_silenced: true,
        })
        .subscribe(() => {
          this.store.dispatch(new toast.ShowSuccessToast('SUCCESS'));
          this.router.navigate(['/login']);
        }, (error) => {
          if (error.error && error.error.new_password1) {
            const messages = PasswordPolicyHelper.flattenMessages(error.error.new_password1);
            this.passwordPolicy.applyMessages(messages);
            this.passwordResetForm.controls['new_password1'].updateValueAndValidity({ emitEvent: false });
            this.errorMessage = '';
          } else if (error.error && error.error.non_field_errors) {
            this.errorMessage = error.error.non_field_errors
            .map(v => {
              if (v === 'Invalid token') {
                return this.translationService.translate('PASSWORD_RESET_EXPIRED');
              } else {
                return this.translationService.translate(v);
              }
            })
            .join('; ');
          } else {
            this.errorMessage = `${error.status} ${error.statusText}`;
          }
          this.cdRef.detectChanges();
        });
    }
  }
}
